import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, Gem } from "lucide-react";

const PROFILES = [
  {
    name: "Aurelia",
    city: "New York",
    tier: "Diamond Elite",
    img: "https://images.unsplash.com/photo-1669213945409-edb075cfd301?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Woman in an elegant black evening dress, editorial studio portrait",
  },
  {
    name: "Vivienne",
    city: "Los Angeles",
    tier: "Platinum Luxe",
    img: "https://images.unsplash.com/photo-1682785077135-4e9389f08c40?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Silhouette of a woman in an evening gown against dramatic light",
  },
  {
    name: "Sloane",
    city: "Miami",
    tier: "Diamond Elite",
    img: "https://images.pexels.com/photos/27132412/pexels-photo-27132412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Studio portrait of a woman in soft dramatic lighting",
  },
  {
    name: "Isabella",
    city: "London",
    tier: "Spotlight Gold",
    img: "https://images.unsplash.com/photo-1724303722588-ca73091c1759?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Dramatic silhouette of a woman in low golden light",
  },
];

export default function DirectoryPreview() {
  const [active, setActive] = useState(null);

  return (
    <section id="directory" data-testid="directory-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 lg:py-32">
      <motion.div
        className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">The Directory</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-amber-100/95">
            A curated circle of <span className="italic gold-text">remarkable women</span>
          </h2>
        </div>
        <p className="text-xs text-neutral-500 uppercase tracking-[0.2em]">Preview — illustrative profiles shown</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {PROFILES.map((p, i) => (
          <motion.button
            key={p.name}
            data-testid={`profile-card-${p.name.toLowerCase()}`}
            onClick={() => setActive(p)}
            className="group relative text-left bg-onyx border hairline-gold card-glow transition-all duration-500 overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src={p.img}
                alt={p.alt}
                loading="lazy"
                className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-transparent p-5 pt-16">
              <div className="flex items-center gap-2 mb-1">
                <Gem size={12} className="text-gold" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-gold/90">{p.tier}</span>
              </div>
              <p className="font-serif text-2xl text-amber-100/95 italic">{p.name}</p>
              <p className="flex items-center gap-1.5 text-xs text-neutral-400 mt-1">
                <MapPin size={11} /> {p.city}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="profile-quickview-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-obsidian/90 backdrop-blur-xl px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <motion.div
              className="glass-panel max-w-md w-full overflow-hidden"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-[4/3]">
                <img src={active.img} alt={active.alt} className="w-full h-full object-cover" />
                <button
                  data-testid="profile-quickview-close"
                  onClick={() => setActive(null)}
                  className="absolute top-3 right-3 p-2 bg-obsidian/70 border hairline-gold text-neutral-300 hover:text-gold transition-colors"
                  aria-label="Close preview"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-6">
                <p className="font-serif text-3xl italic text-amber-100/95">{active.name}</p>
                <p className="flex items-center gap-1.5 text-sm text-neutral-400 mt-1">
                  <MapPin size={13} /> {active.city} · {active.tier}
                </p>
                <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed">
                  Full profiles, verified photography, availability, and direct contact details unlock when the
                  directory launches. This is a design preview.
                </p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-gold/70">Sample profile — illustrative only</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
